import React, { useState, useEffect } from "react";
import CardSection from "../components/CardSection";
import { getAnalytics, getPostPerformance, getCalendarSuggestions, getSubscription } from "../api";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement,
  Title, Tooltip, Legend, ArcElement,
} from "chart.js";
import { useSubscription } from "../SubscriptionContext";
import UsageBadge from "../components/UsageBadge";
import OnboardingBanner from "../components/OnboardingBanner";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// ── Animated counter ─────────────────────────────────────────────────────────
function useCountUp(target, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) { setCount(0); return; }
    let start = 0;
    const step = Math.ceil(target / (duration / 30));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(start);
    }, 30);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

// ── Typing effect ────────────────────────────────────────────────────────────
function TypingText({ texts, speed = 80, pause = 2000 }) {
  const [displayed, setDisplayed] = useState("");
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    const current = texts[textIndex];
    const timeout = setTimeout(() => {
      if (!deleting) {
        setDisplayed(current.substring(0, charIndex + 1));
        if (charIndex + 1 === current.length) {
          setTimeout(() => setDeleting(true), pause);
        } else {
          setCharIndex(charIndex + 1);
        }
      } else {
        setDisplayed(current.substring(0, charIndex - 1));
        if (charIndex - 1 === 0) {
          setDeleting(false);
          setTextIndex((textIndex + 1) % texts.length);
          setCharIndex(0);
        } else {
          setCharIndex(charIndex - 1);
        }
      }
    }, deleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [charIndex, deleting, textIndex, texts, speed, pause]);

  return <span>{displayed}<span className="animate-pulse">|</span></span>;
}

// ── Stat card ────────────────────────────────────────────────────────────────
function AnimatedStatCard({ value, label, icon, color, sublabel }) {
  const animatedValue = useCountUp(value);
  return <StatCard value={animatedValue} label={label} icon={icon} color={color} sublabel={sublabel} />;
}

function StatCard({ value, label, color, sublabel }) {
  const colors = {
    blue:   { border: "border-cyan-500/20", hoverBorder: "hover:border-cyan-500/40", glow: "hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]", text: "text-cyan-400", bar: "bg-gradient-to-r from-cyan-500/60 to-cyan-400/30" },
    purple: { border: "border-purple-500/20", hoverBorder: "hover:border-purple-500/40", glow: "hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]", text: "text-purple-400", bar: "bg-gradient-to-r from-purple-500/60 to-purple-400/30" },
    green:  { border: "border-emerald-500/20", hoverBorder: "hover:border-emerald-500/40", glow: "hover:shadow-[0_0_30px_rgba(16,185,129,0.15)]", text: "text-emerald-400", bar: "bg-gradient-to-r from-emerald-500/60 to-emerald-400/30" },
    amber:  { border: "border-amber-500/20", hoverBorder: "hover:border-amber-500/40", glow: "hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]", text: "text-amber-400", bar: "bg-gradient-to-r from-amber-500/60 to-amber-400/30" },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-[#0d1117]/80 ${c.border} ${c.hoverBorder} ${c.glow} border transition-all duration-500 p-5 group cursor-default`}
      style={{ backdropFilter: "blur(10px)" }}>
      {/* Subtle gradient overlay on hover */}
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      <div className="relative z-10">
        <p className={`text-4xl font-black tabular-nums ${c.text} transition-all duration-300 group-hover:scale-105 origin-left`}>{value}</p>
        <p className="text-xs uppercase tracking-widest text-gray-300 font-semibold mt-2">{label}</p>
        {sublabel && <p className="text-[11px] text-gray-500 mt-1">{sublabel}</p>}
      </div>

      {/* Bottom glow bar */}
      <div className={`absolute bottom-0 left-0 h-[2px] w-full ${c.bar} opacity-40 group-hover:opacity-80 transition-opacity duration-500`} />
    </div>
  );
}

// ── Posting time suggestion card ─────────────────────────────────────────────
const DAY_ICONS = { Monday:"🌅", Tuesday:"📈", Wednesday:"🔥", Thursday:"⚡", Friday:"🎯", Saturday:"🌟", Sunday:"🌙" };

function SuggestionCard({ day, time, reason, index }) {
  const colors = [
    { border: "border-cyan-500/20", hoverBorder: "hover:border-cyan-500/40", glow: "hover:shadow-[0_0_30px_rgba(6,182,212,0.15)]", text: "text-cyan-400", bar: "bg-gradient-to-r from-cyan-500/60 to-cyan-400/30" },
    { border: "border-amber-500/20", hoverBorder: "hover:border-amber-500/40", glow: "hover:shadow-[0_0_30px_rgba(245,158,11,0.15)]", text: "text-amber-400", bar: "bg-gradient-to-r from-amber-500/60 to-amber-400/30" },
    { border: "border-emerald-500/20", hoverBorder: "hover:border-emerald-500/40", glow: "hover:shadow-[0_0_30px_rgba(16,185,129,0.15)]", text: "text-emerald-400", bar: "bg-gradient-to-r from-emerald-500/60 to-emerald-400/30" },
    { border: "border-purple-500/20", hoverBorder: "hover:border-purple-500/40", glow: "hover:shadow-[0_0_30px_rgba(168,85,247,0.15)]", text: "text-purple-400", bar: "bg-gradient-to-r from-purple-500/60 to-purple-400/30" },
  ];
  const c = colors[index % colors.length];
  return (
    <div className={`relative overflow-hidden rounded-2xl bg-[#0d1117]/80 ${c.border} ${c.hoverBorder} ${c.glow} border transition-all duration-500 p-5 group cursor-default`}
      style={{ backdropFilter: "blur(10px)" }}>
      <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      <div className="relative z-10 flex items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-bold text-sm">{day}</p>
            <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${c.text} bg-white/5`}>
              {time}
            </span>
          </div>
          <p className="text-gray-500 text-xs mt-1">{reason}</p>
        </div>
      </div>
      <div className={`absolute bottom-0 left-0 h-[2px] w-full ${c.bar} opacity-40 group-hover:opacity-80 transition-opacity duration-500`} />
    </div>
  );
}

// ── Feature quick-access ─────────────────────────────────────────────────────
function QuickAction({ label, desc, color, onClick }) {
  const colors = {
    blue:   { border: "rgba(6,182,212,0.25)", hoverBorder: "rgba(6,182,212,0.5)", glow: "0 0 30px rgba(6,182,212,0.15)", text: "text-cyan-400" },
    purple: { border: "rgba(168,85,247,0.25)", hoverBorder: "rgba(168,85,247,0.5)", glow: "0 0 30px rgba(168,85,247,0.15)", text: "text-purple-400" },
    green:  { border: "rgba(16,185,129,0.25)", hoverBorder: "rgba(16,185,129,0.5)", glow: "0 0 30px rgba(16,185,129,0.15)", text: "text-emerald-400" },
    orange: { border: "rgba(249,115,22,0.25)", hoverBorder: "rgba(249,115,22,0.5)", glow: "0 0 30px rgba(249,115,22,0.15)", text: "text-orange-400" },
    amber:  { border: "rgba(245,158,11,0.25)", hoverBorder: "rgba(245,158,11,0.5)", glow: "0 0 30px rgba(245,158,11,0.15)", text: "text-amber-400" },
  };
  const c = colors[color] || colors.blue;
  return (
    <button
      onClick={onClick}
      className="relative overflow-hidden flex items-center gap-4 rounded-2xl text-left w-full group cursor-pointer transition-all duration-300 hover:scale-[1.02]"
      style={{
        background: "#000000",
        border: `1px solid ${c.border}`,
        padding: "20px 24px",
        minHeight: "80px"
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = c.hoverBorder;
        e.currentTarget.style.boxShadow = c.glow;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = c.border;
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <div className="relative z-10 flex-1 min-w-0">
        <p className={`text-base font-semibold ${c.text} group-hover:text-white transition-colors`}>{label}</p>
        <p className="text-gray-500 text-sm mt-1">{desc}</p>
      </div>
      <span className={`relative z-10 ${c.text} opacity-50 group-hover:opacity-100 transition-all text-lg group-hover:translate-x-1`}>→</span>
    </button>
  );
}

export default function DashboardTab({ onCtaClick }) {
  const { subscription: subCtx } = useSubscription();
  const [analytics, setAnalytics] = useState(null);
  const [performance, setPerformance] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const isLoggedIn = !!localStorage.getItem("token");

  useEffect(() => {
    const defaults = { scheduledPosts: 0, postedPosts: 0, proposalsGenerated: 0, jobsFound: 0 };
    if (!isLoggedIn) {
      setAnalytics(defaults);
      setSuggestions([
        { day: "Monday",    time: "10:00", reason: "High engagement — start of work week" },
        { day: "Wednesday", time: "14:00", reason: "Mid-week peak — audiences most active" },
        { day: "Friday",    time: "18:00", reason: "Weekend anticipation — shares spike" },
        { day: "Sunday",    time: "20:00", reason: "Relaxed browsing — great for lifestyle content" },
      ]);
      setSubscription({ tier: "free", price: 0 });
      return;
    }
    Promise.allSettled([
      getAnalytics(), getPostPerformance(),
      getCalendarSuggestions(), getSubscription(),
    ]).then(([a, p, s, sub]) => {
      setAnalytics(a.status === "fulfilled" ? a.value.data : defaults);
      setPerformance(p.status === "fulfilled" ? (p.value.data.items || p.value.data || []) : []);
      setSuggestions(s.status === "fulfilled" ? s.value.data : []);
      setSubscription(sub.status === "fulfilled" ? sub.value.data : { tier: "free", price: 0 });
    });
  }, [isLoggedIn]);

  const totalLikes    = performance.reduce((s, p) => s + (p.likes    || 0), 0);
  const totalShares   = performance.reduce((s, p) => s + (p.shares   || 0), 0);
  const totalComments = performance.reduce((s, p) => s + (p.comments || 0), 0);
  const totalViews    = performance.reduce((s, p) => s + (p.views    || 0), 0);
  const totalEngagement = totalLikes + totalShares + totalComments;

  const tier = subscription?.tier || "free";
  const tierColors = {
    free:         "text-gray-400",
    professional: "text-cyan-400",
    business:     "text-purple-400",
    enterprise:   "text-amber-400",
  };

  return (
    <div className="space-y-8">
      {isLoggedIn && <OnboardingBanner onNavigate={onCtaClick} />}

      {/* ── Hero ── */}
      <div className="relative overflow-hidden rounded-2xl border border-white/8 bg-gradient-to-br from-gray-900 via-[#0d1117] to-gray-900 px-6 py-8 sm:py-10 text-center">
        <div className="absolute inset-0 hero-gradient-animate opacity-30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(100,180,255,0.08),transparent)]" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-px h-16 bg-gradient-to-b from-cyan-500/50 to-transparent" />

        {/* Current Plan Badge - Top Right */}
        {isLoggedIn && subscription && (
          <div className="absolute top-4 right-4 z-10">
            <div className={`px-3 py-1.5 rounded-lg border text-xs font-bold uppercase tracking-wider flex items-center gap-2 ${
              tier === 'enterprise' ? 'bg-amber-500/15 border-amber-500/30 text-amber-400' :
              tier === 'business' ? 'bg-purple-500/15 border-purple-500/30 text-purple-400' :
              tier === 'professional' ? 'bg-cyan-500/15 border-cyan-500/30 text-cyan-400' :
              'bg-gray-500/15 border-gray-500/30 text-gray-400'
            }`}>
              <span className={`w-2 h-2 rounded-full ${
                tier === 'enterprise' ? 'bg-amber-400' :
                tier === 'business' ? 'bg-purple-400' :
                tier === 'professional' ? 'bg-cyan-400' :
                'bg-gray-400'
              }`} />
              {tier} Plan
            </div>
          </div>
        )}

        <div className="relative">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-semibold mb-4 uppercase tracking-wider">
            <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
            AI Command Center
          </div>
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-black tracking-tight mb-3">
            <span className="text-white">
              Welcome to{" "}
            </span>
            <span className="text-cyan-400">
              Kronos AI
            </span>
          </h1>
          <p className="text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto leading-relaxed min-h-[28px]">
            <TypingText texts={[
              "Automate your phone calls with AI receptionist...",
              "Generate winning proposals in seconds...",
              "Create & schedule social media content...",
              "Scale your business with AI automation..."
            ]} />
          </p>
        </div>
      </div>

      {/* ── Social Proof ── */}
      <div className="relative overflow-hidden flex flex-col items-center gap-3 sm:gap-4 py-4 sm:py-5 rounded-2xl border border-white/[0.06] bg-[#0d1117]/60 hover:border-white/10 transition-all duration-500"
        style={{ backdropFilter: "blur(10px)" }}>
        <div className="absolute inset-0 bg-gradient-to-br from-white/[0.01] to-transparent" />
        <p className="relative z-10 text-xs sm:text-sm text-gray-500 uppercase tracking-widest font-semibold">Powered by Industry Leaders</p>
        <div className="relative z-10 flex items-center gap-4 sm:gap-8 flex-wrap justify-center px-4">
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Google Gemini</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12 0C5.372 0 0 5.373 0 12s5.372 12 12 12 12-5.373 12-12S18.628 0 12 0zm-.5 3.5h1v3.25l2.813 1.625-.5.866L12 7.25V3.5zm-5.75 5h3.5v1h-3.5v-1zm0 6h3.5v1h-3.5v-1zm11.5-3h-3.5v-1h3.5v1z" fill="#4285F4"/><path d="M19.35 10.04A7.49 7.49 0 0012 4.5a7.49 7.49 0 00-7.35 5.54A4.5 4.5 0 005 19.5h13.5a3.75 3.75 0 00.85-7.46z" fill="#4285F4"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Google Cloud</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M13.23 10.56V10c0-1.94 1.4-3.42 3.38-3.42 1.97 0 3.41 1.48 3.41 3.42v.56h1.49V10c0-2.8-2.05-4.9-4.9-4.9-2.83 0-4.87 2.1-4.87 4.9v.56h1.49z" fill="#FF9900"/><path d="M6.5 10.56V10c0-2.8 2.04-4.9 4.87-4.9v1.48c-1.97 0-3.38 1.48-3.38 3.42v.56H6.5z" fill="#FF9900"/><path d="M3.58 13.72v2.97c0 2.18 1.77 3.95 3.95 3.95h8.94c2.18 0 3.95-1.77 3.95-3.95v-2.97H3.58z" fill="#FF9900"/><rect x="3.58" y="10.56" width="16.84" height="3.16" rx="1" fill="#FF9900"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">AWS Bedrock</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#00D4AA" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">BytePlus AI</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm3.248 18.348l-.003-.01-1.399-4.166-4.727 1.456 1.399 4.17.003.007a8.49 8.49 0 01-4.033-2.07l3.032-3.697-3.452-3.296-3.032 3.697A8.49 8.49 0 013.51 9.99l4.727-1.456L6.84 4.364A8.49 8.49 0 0112 3.51c1.48 0 2.88.38 4.096 1.046L12.16 8.07l3.453 3.297 3.937-4.514a8.49 8.49 0 011.94 4.448l-4.728 1.456 1.399 4.17a8.49 8.49 0 01-2.913 1.421z" fill="#F22F46"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Twilio</span>
          </span>
          <span className="text-gray-600 hidden sm:inline">•</span>
          <span className="flex items-center gap-1.5">
            <svg className="w-4 h-4" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" fill="#2C2C2C" stroke="#5B5FC7" strokeWidth="1.5"/><path d="M8 9.5c0-.83.67-1.5 1.5-1.5h5c.83 0 1.5.67 1.5 1.5v5c0 .83-.67 1.5-1.5 1.5h-5c-.83 0-1.5-.67-1.5-1.5v-5z" fill="#5B5FC7"/><path d="M10.5 11.5l1.5 1.5 2-2" stroke="white" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-gray-300 text-xs sm:text-sm font-bold">Retell AI</span>
          </span>
        </div>
      </div>

      {/* ── AI Engines Badge Row - Mobile Responsive ── */}
      <p className="text-xs text-gray-500 uppercase tracking-widest font-semibold text-center mb-2">Primary AI Models</p>
      <div className="flex justify-center px-4">
        <div className="grid grid-cols-2 sm:inline-flex sm:items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-2.5 rounded-2xl border border-white/[0.06] bg-[#0d1117]/60 hover:border-white/10 transition-all duration-500 w-full sm:w-auto"
          style={{ backdropFilter: "blur(10px)" }}>
          {/* Google Gemini */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">Gemini 3.1 Pro</span>
          </span>
          <span className="hidden sm:inline text-gray-700">|</span>
          {/* Claude */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="#D97757"/><path d="M12 6c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z" fill="#D97757"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">Claude Sonnet</span>
          </span>
          <span className="hidden sm:inline text-gray-700">|</span>
          {/* ByteDance */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="#00D4AA" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">ByteDance</span>
          </span>
          <span className="hidden sm:inline text-gray-700">|</span>
          {/* Dreamina */}
          <span className="flex items-center gap-1.5 justify-center sm:justify-start">
            <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24"><defs><linearGradient id="dreamina-grad" x1="0%" y1="0%" x2="100%" y2="100%"><stop offset="0%" stopColor="#FF6B9D"/><stop offset="100%" stopColor="#C44DFF"/></linearGradient></defs><circle cx="12" cy="12" r="10" fill="url(#dreamina-grad)"/><path d="M8 12c0-2.2 1.8-4 4-4s4 1.8 4 4-1.8 4-4 4" stroke="white" strokeWidth="1.5" fill="none" strokeLinecap="round"/><circle cx="12" cy="12" r="1.5" fill="white"/></svg>
            <span className="text-[10px] sm:text-[11px] font-bold text-white whitespace-nowrap">Dreamina</span>
          </span>
        </div>
      </div>

      {/* ── Quick actions ── */}
      <div>
        <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Quick Access</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <QuickAction label="Social Media" desc="Generate & schedule posts" color="blue" onClick={() => onCtaClick?.("social-media")} />
          <QuickAction label="AI Receptionist" desc="Never miss a call" color="purple" onClick={() => onCtaClick?.("ai-receptionist")} />
          <QuickAction label="Proposals" desc="Find jobs & win clients" color="green" onClick={() => onCtaClick?.("proposals")} />
          <QuickAction label="Fable AI" desc="Create documents & strategies" color="orange" onClick={() => onCtaClick?.("fable-ai")} />
          <QuickAction label="Upgrade Plan" desc={tier === "enterprise" ? "You're on the top plan" : "Unlock more features"} color="amber" onClick={() => onCtaClick?.("pricing")} />
        </div>
      </div>

      {/* ── Stats row ── */}
      {analytics && (
        <div>
          <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Activity Overview</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <StatCard value={analytics.scheduledPosts}    label="Scheduled Posts"      color="blue"   sublabel="Queued for publishing" />
            <StatCard value={analytics.postedPosts}       label="Published Posts"       color="purple" sublabel="Successfully posted" />
            <StatCard value={analytics.proposalsGenerated}label="Proposals Generated"   color="green"  sublabel="AI-written proposals" />
            <StatCard value={analytics.jobsFound}         label="Jobs Found"            color="amber"  sublabel="Active opportunities" />
          </div>
        </div>
      )}

      {/* ── Subscription + Engagement side by side ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

        {/* Subscription panel — 2/3 width */}
        {subscription && (
          <div className="lg:col-span-2 relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-cyan-500/20 hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(6,182,212,0.1)] transition-all duration-500 p-5 space-y-4"
            style={{ backdropFilter: "blur(10px)" }}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <div className="relative z-10 flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                  </svg>
                </div>
                <div>
                  <p className={`text-base font-bold capitalize ${tierColors[tier] || "text-white"}`}>
                    {tier} Plan
                  </p>
                  <p className="text-gray-500 text-xs">${subscription.price}/month</p>
                </div>
              </div>
              {tier !== "enterprise" && (
                <button
                  onClick={() => onCtaClick?.("pricing")}
                  className="px-4 py-2 rounded-xl bg-white/5 border border-cyan-500/30 text-cyan-400 text-xs font-bold hover:bg-cyan-500/10 hover:border-cyan-500/50 transition-all">
                  Upgrade Plan →
                </button>
              )}
            </div>
            {subCtx?.usage ? (
              <div className="relative z-10 grid grid-cols-2 sm:grid-cols-3 gap-3">
                <UsageBadge label="Posts"     used={subCtx.usage.posts.used}           limit={subCtx.usage.posts.limit} />
                <UsageBadge label="Images"    used={subCtx.usage.images.used}          limit={subCtx.usage.images.limit} />
                <UsageBadge label="Videos"    used={subCtx.usage.videos.used}          limit={subCtx.usage.videos.limit} />
                <UsageBadge label="Proposals" used={subCtx.usage.proposals.used}       limit={subCtx.usage.proposals.limit} />
                <UsageBadge label="Accounts"  used={subCtx.usage.social_accounts.used} limit={subCtx.usage.social_accounts.limit} />
                <UsageBadge label="Scheduled" used={subCtx.usage.scheduled_posts.used} limit={subCtx.usage.scheduled_posts.limit} />
              </div>
            ) : (
              <p className="text-gray-600 text-sm">Log in to see your usage</p>
            )}
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-cyan-500/60 to-cyan-400/30 opacity-40" />
          </div>
        )}

        {/* Engagement donut — 1/3 width */}
        {performance.length > 0 && totalEngagement > 0 ? (
          <div className="relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-purple-500/20 hover:border-purple-500/40 hover:shadow-[0_0_30px_rgba(168,85,247,0.1)] transition-all duration-500 p-5 flex flex-col items-center justify-center gap-3"
            style={{ backdropFilter: "blur(10px)" }}>
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.02] to-transparent opacity-0 hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
            <p className="relative z-10 text-xs text-gray-500 uppercase tracking-widest font-semibold">Total Engagement</p>
            <div className="relative z-10 w-32 h-32">
              <Doughnut
                data={{
                  labels: ["Likes", "Shares", "Comments"],
                  datasets: [{
                    data: [totalLikes, totalShares, totalComments],
                    backgroundColor: ["rgba(6,182,212,0.8)","rgba(168,85,247,0.8)","rgba(16,185,129,0.8)"],
                    borderColor: ["rgb(6,182,212)","rgb(168,85,247)","rgb(16,185,129)"],
                    borderWidth: 2,
                  }]
                }}
                options={{ plugins: { legend: { display: false } }, cutout: "70%" }}
              />
            </div>
            <div className="relative z-10 text-center">
              <p className="text-2xl font-black text-purple-400">{totalEngagement.toLocaleString()}</p>
              <p className="text-xs text-gray-500">interactions</p>
            </div>
            <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-purple-500/60 to-purple-400/30 opacity-40" />
          </div>
        ) : !performance.length && subscription ? (
          <div className="relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-white/10 border-dashed hover:border-white/20 transition-all duration-500 p-5 flex flex-col items-center justify-center gap-2 text-center"
            style={{ backdropFilter: "blur(10px)" }}>
            <svg className="w-8 h-8 text-gray-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M3 3v18h18"/><path d="M18 17V9"/><path d="M13 17V5"/><path d="M8 17v-3"/>
            </svg>
            <p className="text-gray-500 text-sm font-medium">No engagement data yet</p>
            <p className="text-gray-600 text-xs">Post content to see analytics here</p>
            <button onClick={() => onCtaClick?.("social-media")} className="mt-1 text-xs text-cyan-400 hover:text-cyan-300">
              Start posting →
            </button>
          </div>
        ) : null}
      </div>

      {/* ── Bar chart (only when data exists) ── */}
      {performance.length > 0 && (
        <div className="relative overflow-hidden rounded-2xl bg-[#0d1117]/80 border border-emerald-500/20 hover:border-emerald-500/40 hover:shadow-[0_0_30px_rgba(16,185,129,0.1)] transition-all duration-500 p-5"
          style={{ backdropFilter: "blur(10px)" }}>
          <div className="absolute bottom-0 left-0 h-[2px] w-full bg-gradient-to-r from-emerald-500/60 to-emerald-400/30 opacity-40" />
          <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-4">Post Performance</h2>
          <Bar
            data={{
              labels: ["Likes", "Shares", "Comments", "Views"],
              datasets: [{
                label: "Engagement",
                data: [totalLikes, totalShares, totalComments, totalViews],
                backgroundColor: ["rgba(6,182,212,0.7)","rgba(168,85,247,0.7)","rgba(16,185,129,0.7)","rgba(251,146,60,0.7)"],
                borderColor:      ["rgb(6,182,212)","rgb(168,85,247)","rgb(16,185,129)","rgb(251,146,60)"],
                borderWidth: 2, borderRadius: 8,
              }]
            }}
            options={{
              responsive: true,
              plugins: {
                legend: { display: false },
                title: { display: false },
              },
              scales: {
                y: { ticks: { color: "#6b7280" }, grid: { color: "rgba(255,255,255,0.05)" } },
                x: { ticks: { color: "#6b7280" }, grid: { display: false } },
              }
            }}
          />
        </div>
      )}

      {/* ── AI Posting Suggestions ── */}
      {suggestions.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold">AI Best Posting Times</h2>
            <span className="text-xs text-gray-600">Based on engagement patterns</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {suggestions.map((s, i) => (
              <SuggestionCard key={i} index={i} day={s.day} time={s.time} reason={s.reason} />
            ))}
          </div>
        </div>
      )}

      {/* ── Feature cards ── */}
      <div>
        <h2 className="text-xs text-gray-500 uppercase tracking-widest font-semibold mb-3">Platform Features</h2>
        <CardSection
          heading=""
          subtitle=""
          cardData={[
            { heading: "AI Receptionist",       description: <span className="text-sm text-gray-300">24/7 intelligent phone assistant that handles calls, books appointments, and answers customer queries automatically.</span>, cta: "Setup Now", hue: 280, ctaAction: "ai-receptionist" },
            { heading: "Social Media Hub",       description: <span className="text-sm text-gray-300">AI-powered content creation, stunning visuals, and automated scheduling across all your social channels.</span>, cta: "Open Hub", hue: 210, ctaAction: "social-media" },
            { heading: "Proposal Intelligence", description: <span className="text-sm text-gray-300">Find high-value freelance jobs and generate personalized, winning proposals in seconds with AI.</span>, cta: "Find Jobs", hue: 165, ctaAction: "proposals" },
            { heading: "Fable AI",              description: <span className="text-sm text-gray-300">Craft enterprise-grade documents, business plans, contracts, and strategies with mythical precision using Claude AI.</span>, cta: "Create Now", hue: 25, ctaAction: "canvas" },
            { heading: "Upgrade Your Plan",     description: <span className="text-sm text-gray-300">Unlock unlimited posts, video generation, more social accounts, and priority support.</span>, cta: "View Plans", hue: 338, ctaAction: "pricing" },
          ]}
          onCtaClick={onCtaClick}
        />
      </div>
    </div>
  );
}
